import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useVsiAppContext } from "../../../../stores/VsiApp";
import {
  VsiBubble,
  VsiH4,
  VsiH5,
  VsiLgText,
  VsiMedText,
  VsiSpanText
} from "../../../../styled/App.styled";
import Grid from "../../../../components/Grid";
import { Row, Col, Badge, Card, Spinner } from "reactstrap";
import { clusterSummaryModelDefs } from "../../../../utils/gridColumnDefs/virtual/clusterSummary";
import { datastoreDefs, vmDefs, hostsDefs } from "../../../../utils/gridColumnDefs/virtual/allUnits";
import AtAGlance from "../../../../components/AtAGlance";
import ProgressBar from "../../../../components/ProgressBar";
import Stockchart from "../../../../components/Stockchart";
import StackedColumnChart from "../../../../components/StackedColumnChart";
import { useAuthWithRoute, useDataWithRoute, useTitleFromComponent } from "../../../../utils/hooks";
import { formatStockChartConfig, getColor } from "../../../../utils/Helpers";
import styles from "../../../../utils/themeStyles";
import VsiSimpleGauge from "../../../../components/VsiSimpleGauge";
import themeStyles from "../../../../utils/themeStyles";

const chartHeight = 350;
const gaugeHeight = 300;

const clusterSummaryAag = [
  {
    "field": "vms_per_host_ratio",
    "style": {
      "color": "blue"
    }
  },
  {
    "field": "average_vm_cpu_size",
    "style": {
      "color": "blue"
    }
  },
  {
    "field": "average_vm_memory_size",
    "style": {
      "color": "blue"
    }
  },
  {
    "field": "average_vm_disk_size",
    "style": {
      "color": "blue"
    }
  },
  {
    "field": "vm_cpu_build_capacity",
    "style": {
      "color": "purple"
    }
  },
  {
    "field": "vm_memory_build_capacity",
    "style": {
      "color": "purple"
    }
  },
  {
    "field": "vm_disk_build_capacity",
    "style": {
      "color": "purple"
    }
  }
];

const clusterSummaryAagTwo = [
  {
    "field": "num_esx_hosts",
    "icon": true,
    "mobile": true
  },
  {
    "field": "number_of_cpus",
    "icon": true,
    "mobile": true
  },
  {
    "field": "cpus_in_use",
    "icon": true,
    "mobile": true
  },
  {
    "field": "total_ghz",
    "icon": true,
    "mobile": true
  },
  {
    "field": "total_memory",
    "icon": true,
    "mobile": true
  },
  {
    "field": "total_datastore_allocation",
    "icon": true,
    "mobile": true
  },
  {
    "title": "Weeks Left To Capacity",
    "field": "days_left_to_capacity",
    "style": {
      "color": "purple"
    }
  },
  {
    "field": "drs_fully_enabled",
    "icon": true,
    "mobile": true
  }
];

const categories = ["CPU", "Memory", "Disk"];

export default function ClusterSummary({ context, title }) {
  const { theme, client, periodId } = useVsiAppContext();
  const [searchParams] = useSearchParams();
  useTitleFromComponent(`${decodeURIComponent(searchParams.get("cluster_name")) || "N/A"} - ${title}`);
  // I am aggregating all 6 (six) stored procedure into one, so instead of 6 api/db calls we have only one for performance reasons.
  const [data, { isLoading }] = useDataWithRoute(`/api/v6/report/virtual_cluster_summary_proc?client_id=${client.clientId}&period_id=${periodId}&vcenter_name=${encodeURI(searchParams.get("vcenter_name"))}&cluster_name=${encodeURI(searchParams.get("cluster_name"))}`);

  const [savedPlanData, {
    isLoading: isPlanDataLoading, reFetchData: reFetchSavedPlanData, setData: setSavedPlanData
  }] = useAuthWithRoute(`/api/v6/admin/virtual_cluster_capacity_plan/${client.clientId}/${encodeURI(searchParams.get("vcenter_name"))}/${encodeURI(searchParams.get("cluster_name"))}`);

  const [hostGrid, setHostGrid] = useState([]);
  const [vmGrid, setVmGrid] = useState([]);
  const [datastoreGrid, setDatastoreGrid] = useState([]);
  const [aagData, setAagData] = useState({});
  const [trendData, setTrendData] = useState([]);
  const [utilBarData, setUtilBarData] = useState([]);
  const [gaugeSeries, setGaugeSeries] = useState([]);
  const [vsiCapacityScore, setVsiCapacityScore] = useState("...");
  const [capacityInsight, setCapacityInsight] = useState([]);
  const [formattedPlan, setFormattedPlan] = useState([]);

  let navigate = useNavigate();

  const onVmGridClick = ({ data }) => {
    navigate(`/reports/virtual/vm/vm-summary?vm_name=${encodeURI(data.vm_name)}&vcenter_name=${encodeURI(searchParams.get("vcenter_name"))}&cluster_name=${encodeURI(searchParams.get("cluster_name"))}&esx_host_name=${encodeURI(data.esx_host_name)}&period_id=${periodId}`);
  };

  const onHostGridClick = ({ data }) => {
    navigate(`/reports/virtual/host/host-summary?esx_host_name=${encodeURI(data.esx_host_name)}&vcenter_name=${encodeURI(searchParams.get("vcenter_name"))}&cluster_name=${encodeURI(searchParams.get("cluster_name"))}&period_id=${periodId}`);
  };

  const onDatastoreGridClick = ({ data }) => {
    navigate(`/reports/virtual/datastore/datastore-summary?datastore_name=${encodeURI(data.datastore_name)}&vcenter_name=${encodeURI(searchParams.get("vcenter_name"))}&period_id=${periodId}`);
  };

  const onClusterModelingClick = () => {
    navigate(`/reports/virtual/cluster/cluster-modeling?period_id=${periodId}&vcenter_name=${encodeURI(searchParams.get("vcenter_name"))}&cluster_name=${encodeURI(searchParams.get("cluster_name"))}`);
  };

  // Break apart for performance
  useEffect(() => {

    setHostGrid(data?.recordsets?.[1] || []);
    setVmGrid(data?.recordsets?.[2] || []);
    setDatastoreGrid(data?.recordsets?.[3] || []);
    setAagData(data?.recordsets?.[0]?.[0] || []);
    setUtilBarData(categories.map(cat => parseInt((data?.recordsets?.[0]?.[0]?.[`${cat.toLowerCase()}_util_pct`] || 0) * 100)));

    let sortedData = (data?.recordsets?.[4] || []).sort((a, b) => new Date(a.collection_date) - new Date(b.collection_date));
    setTrendData(formatStockChartConfig(sortedData, [
      {
        name: "VM Build Capacity",
        field: "vm_build_capacity"
      }
    ]).series);

    processVirtualVMList(data?.recordsets?.[2]);

    processPlanData();

  }, [data, savedPlanData, theme]);

  const processVirtualVMList = (records) => {
    let capacityScore = 0;
    let capacityInsightDict = {
      under40: 0,
      over60: 0,
      ok: 0
    };

    const capacityInsightArr = [];

    (records || []).forEach(val => {
      const score = (val?.vsi_capacity_score) || 0;
      capacityScore += score;
      if (score < 40) {
        capacityInsightDict.under40 += 1;
      } else if (score > 60) {
        capacityInsightDict.over60 += 1;
      } else {
        capacityInsightDict.ok += 1;
      }
    });

    capacityScore = parseInt((capacityScore / (records || [0]).length).toFixed(0));

    if (capacityInsightDict.under40 > 0) {
      capacityInsightArr.push({
        message: `VMs under 40`,
        color: "danger",
        value: capacityInsightDict.under40
      });
    }

    if (capacityInsightDict.over60 > 0) {
      capacityInsightArr.push({
        message: `VMs over 60`,
        color: "danger",
        value: capacityInsightDict.over60
      });
    }

    if (capacityInsightDict.ok > 0) {
      capacityInsightArr.push({
        message: `VMs ok!`,
        color: "success",
        value: capacityInsightDict.ok
      });
    }


    setCapacityInsight(capacityInsightArr);
    setGaugeSeries([{
      name: "",
      data: [

        {
          y: 0,
          name: "0",
          connectorWidth: 0,
          dataLabels: {
            align: "left",
            enabled: true,
            x: -10,
            y: 10
          }
        },
        {
          name: "Capacity Score",
          y: capacityScore,
          color: getColor(capacityScore),
          connectorWidth: 0
        },
        {
          name: "N/A",
          y: (100 - capacityScore),
          allowPointSelect: false,
          color: styles[theme].cardBgColor,
          dataLabels: {
            enabled: false,
            alignTo: "plotEdges"
          }
        },
        {
          y: 0,
          name: "100",
          connectorWidth: 0,
          dataLabels: {
            enabled: true,
            align: "right",
            x: 5,
            y: 10
          }
        }
      ],
      states: {
        hover: {
          enabled: false
        }
      },
      innerSize: "80%",
      dial: {
        backgroundColor: themeStyles[theme].altBgColor,
        baseWidth: 5
      },
      pivot: {
        backgroundColor: themeStyles[theme].altBgColor,
        radius: 6
      },
      dataLabels: {
        borderWidth: 0,
        color: styles[theme].textColor
      }
    }]);
    setVsiCapacityScore(isNaN(capacityScore) ? "..." : capacityScore.toString());
  };

  const processPlanData = () => {
    if (savedPlanData && data?.recordsets?.[5]) {
      const newPlanData = [];
      const uniqueHostDict = {};
      (data?.recordsets?.[5] ?? []).forEach(host => {
        if (!uniqueHostDict[host.model]) {
          uniqueHostDict[host.model] = {};
        }
        uniqueHostDict[host.model] = {
          ...host
        };
      });

      savedPlanData.forEach(datum => {
        (datum?.adjustments ?? []).forEach(adj => {
          const newPlan = {
            notes: datum.notes,
            projectName: datum.projectName,
            date: adj.actualDate
          };
          if (adj?.customCapacity) {
            let capObj = {
              ...newPlan,
              type: "Capacity"
            };
            Object.keys(adj.customCapacity).forEach(capKey => {
              capObj[capKey] = adj.customCapacity[capKey];
            });
            newPlanData.push(capObj);
          }
          if (adj?.customWorkload) {
            let workObj = {
              ...newPlan,
              type: "Workload"
            };
            Object.keys(adj.customWorkload).forEach(workKey => {
              workObj[workKey] = adj.customWorkload[workKey];
            });
            newPlanData.push(workObj);
          }

          if (adj?.models?.length) {
            let modelObj = {
              ...newPlan,
              type: "Capacity"
            };
            (adj?.models ?? []).forEach(model => {
              const host = uniqueHostDict[model.model];
              if (host) {
                const {
                  cpus,
                  memory_size,
                  disk_size
                } = host;
                modelObj.cpu = parseFloat(cpus * model.modelCountAdded);
                modelObj.mem = parseFloat(memory_size * model.modelCountAdded);
                modelObj.disk = parseFloat(disk_size * model.modelCountAdded);
                modelObj.cost = parseFloat(parseFloat(datum?.hostCost ?? 0) * model.modelCountAdded);
                modelObj.model = model.model;
              }
            });
            newPlanData.push(modelObj);
          }
        });
      });
      setFormattedPlan(newPlanData);
    }
  };

  const util = parseFloat(((aagData?.vm_util_under_10 / aagData?.num_vms) * 100).toFixed(2));

  return (
    <Card className={`report-card p-2 nav-card-${theme}`}>
      <VsiH4>Cluster Summary - {decodeURIComponent(searchParams.get("cluster_name")) || "N/A"}</VsiH4>
      {(isPlanDataLoading || isLoading) ? (
        <Spinner color={"success"} />
      ) : <>
        <VsiBubble noAutobot>
          <Row>
            <Col lg={12} xl={4}>
              <VsiBubble autobotName="SimpleGauge" autobotInfo={{
                data: gaugeSeries,
                insight: capacityInsight,
                numVms: aagData?.num_vms,
                title: "Capacity Score"
              }}>
                <VsiH5 className={"pe-5 mb-5"}>Capacity Score</VsiH5>
                <VsiSimpleGauge title={vsiCapacityScore}
                                series={gaugeSeries}
                                height={gaugeHeight} />
                <div
                  className={`d-flex flex-column align-items-center justify-content-${capacityInsight.length > 1 ? "around" : "between"}`}>
                  {capacityInsight.map((insight, i) => (
                    <VsiMedText key={`capacity-insight-${i}-${insight?.value}`}>
                      <Badge pill color={insight?.color}>
                        {insight.value}
                      </Badge>
                      {" "}
                      {insight.message}
                    </VsiMedText>
                  ))}
                  <VsiLgText className={"mt-2"}>
                    Total VMs: <b>{aagData?.num_vms}</b>
                  </VsiLgText>
                </div>
              </VsiBubble>
            </Col>
            <Col lg={12} xl={4}>
              <VsiBubble autobotName="MixedUtilChart" autobotInfo={{
                data: {
                  ProgressBar: {
                    data: util, config: {
                      fields: ["percent_used"],
                      thresholds: [[80, "#008080"]],
                      denominator: aagData?.num_vms,
                      title: "% Used of Usable"
                    }, graphType: "ProgressBar"
                  },
                  StackedColumnChart: {
                    data: utilBarData,
                    lines: categories,
                    yAxisTitle: "Utilization (%)",
                    graphType: "StackedColumnChart"
                  },
                  title: "Utilization",
                  vms_under_10: `${aagData?.vm_util_under_10 || 0} / ${aagData?.num_vms || 0}`
                }
              }}>
                <div className={"d-flex align-items-top justify-content-between"}>
                  <div>
                    <VsiH5 className={"pe-5"}>Utilization</VsiH5>
                    <VsiMedText
                      className={"pe-2"}>{`${aagData?.vm_util_under_10 || 0} / ${aagData?.num_vms || 0} VMs under 10% utilization`}</VsiMedText>
                  </div>
                  <VsiH4>{util ? `${util}%` : "N/A"}</VsiH4>
                </div>
                <div className={"mb-5"}>
                  <ProgressBar data={[util || 0]}
                               denominator={aagData?.num_vms || 0}
                               colors={[]}
                               disableStat
                               thresholds={[
                                 [80, "#008080"]
                               ]}
                               size={"sm"} />
                </div>
                <StackedColumnChart yAxisTitle={"Utilization (%)"}
                                    categories={categories}
                                    disableLegend={true}
                                    data={utilBarData}
                                    height={chartHeight} />

              </VsiBubble>
            </Col>
            <Col lg={12} xl={4}>
              <VsiBubble autobotName="Stockchart" autobotInfo={{
                data: trendData,
                lines: ["VM Build Capacity"],
                yAxisTitle: "# VMs",
                constraint: aagData?.capacity_constrain_str,
                title: "Build Capacity"
              }}>
                <VsiH5 className={"pe-5"}>Build Capacity</VsiH5>
                <div className={"mb-5"}>
                  <VsiSpanText className={"pe-2"}>
                    <VsiMedText className={"pe-2"}>Constraint:</VsiMedText>
                    <span className={"fst-italic"}>{aagData?.capacity_constrain_str || "N/A"}</span>
                  </VsiSpanText>
                </div>
                <Stockchart height={chartHeight}
                            series={trendData}
                            saveParams={{
                              clusterName: searchParams.get("cluster_name"),
                              vcenterName: searchParams.get("vcenter_name")
                            }}
                            navDisabled
                            yAxisTitle={"# VMs"} />
              </VsiBubble>
            </Col>
          </Row>
        </VsiBubble>
        <VsiH4>
          At a Glance
        </VsiH4>
        <VsiBubble autobotInfo={{
          data: {
            aag1: { data: aagData, config: clusterSummaryAag },
            aag2: { data: aagData, config: clusterSummaryAagTwo }
          }
        }}>
          <AtAGlance config={clusterSummaryAag}
                     data={aagData} size={"lg"} />
          <br />
          <AtAGlance config={clusterSummaryAagTwo}
                     data={aagData} size={"lg"} />
        </VsiBubble>
        {(isPlanDataLoading) ? (
          <Spinner color={"success"} />
        ) : formattedPlan.length ? (
          <>
            <VsiH4 onClick={onClusterModelingClick} style={{ cursor: "pointer" }}>
              Cluster Modeling
            </VsiH4>
            <VsiBubble>
              <Grid addStyle
                    data={formattedPlan}
                    saveParams={{ metric: "cluster-model" }}
                    columnDefs={clusterSummaryModelDefs} />
            </VsiBubble>
          </>
        ) : null}
        <VsiH4>
          ESX Hosts
        </VsiH4>
        <VsiBubble autobotName={"ESXHostsGrid"}>
          <Grid addStyle
                gridText={"Double click a host to see more details"}
                data={hostGrid}
                saveParams={{ metric: "esx-hosts" }}
                rowDoubleClicked={onHostGridClick}
                columnDefs={hostsDefs} />
        </VsiBubble>
        <VsiH4>
          Virtual Machines
        </VsiH4>
        <VsiBubble autobotName={"VMGrid"}>
          <Grid addStyle
                gridText={"Double click a VM to see more details"}
                data={vmGrid}
                saveParams={{ metric: "virtual-machines" }}
                rowDoubleClicked={onVmGridClick}
                columnDefs={vmDefs} />
        </VsiBubble>
        <VsiH4 className={"mt-4"}>
          Datastores
        </VsiH4>
        <VsiBubble autobotName={"DatastoresGrid"}>
          <Grid addStyle
                gridText={"Double click a datastore to see more details"}
                data={datastoreGrid}
                saveParams={{ metric: "datastores" }}
                rowDoubleClicked={onDatastoreGridClick}
                columnDefs={datastoreDefs} />
        </VsiBubble>
      </>}
    </Card>
  );
}
